'use client';
import { useState, useRef, useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { UseOcrImportResult } from './useOcrImport';

interface UseOcrFormSyncOptions<TSample, TFormData> {
	ocr: UseOcrImportResult<TSample>;
	form: TFormData;
	populate: (data: TFormData) => void;
	clearForm: () => void;
	mapSampleToForm: (base: TFormData, sample: TSample) => TFormData;
	analysisId: string | undefined;
	isLoading: boolean;
	onLoadingChange: (loading: boolean) => void;
	onOcrBusyChange?: (busy: boolean) => void;
}

export const useOcrFormSync = <TSample, TFormData>({
	ocr,
	form,
	populate,
	clearForm,
	mapSampleToForm,
	analysisId,
	isLoading,
	onLoadingChange,
	onOcrBusyChange,
}: UseOcrFormSyncOptions<TSample, TFormData>) => {
	const [filledOcrJobId, setFilledOcrJobId] = useState('');
	const [filledOcrSampleIndex, setFilledOcrSampleIndex] = useState(-1);
	const carryOverRef = useRef<TFormData | null>(null) as MutableRefObject<TFormData | null>;

	useEffect(() => {
		onLoadingChange(isLoading);
	}, [isLoading, onLoadingChange]);

	useEffect(() => {
		onOcrBusyChange?.(ocr.isBusy);
	}, [ocr.isBusy, onOcrBusyChange]);

	const handleOcrFileSelect = async (file: File) => {
		setFilledOcrJobId('');
		setFilledOcrSampleIndex(-1);
		carryOverRef.current = null;
		clearForm();
		await ocr.uploadFile(file);
	};

	useEffect(() => {
		if (ocr.status !== 'done') return;
		if (ocr.jobId === filledOcrJobId && ocr.currentIndex === filledOcrSampleIndex) return;
		const sample = ocr.currentSample;
		if (!sample) return;
		const base = carryOverRef.current ?? form;
		populate(mapSampleToForm(base, sample));
		carryOverRef.current = null;
		setFilledOcrJobId(ocr.jobId);
		setFilledOcrSampleIndex(ocr.currentIndex);
	}, [
		ocr.status,
		ocr.jobId,
		ocr.currentIndex,
		ocr.currentSample,
		filledOcrJobId,
		filledOcrSampleIndex,
		form,
		populate,
		mapSampleToForm,
	]);

	const totalOcrSamples = ocr.totalSamples;
	const showOcrCounter = !analysisId && totalOcrSamples > 1;
	const currentOcrSample = Math.min(ocr.currentIndex + 1, totalOcrSamples);
	const ocrProgress = totalOcrSamples > 0 ? (currentOcrSample / totalOcrSamples) * 100 : 0;

	return {
		carryOverRef,
		handleOcrFileSelect,
		ocrLoadingMessage: ocr.loadingMessage,
		totalOcrSamples,
		showOcrCounter,
		currentOcrSample,
		ocrProgress,
	};
};
